import React, { useEffect, useRef, useState } from "react";
import HookForm from "../HookForm/HookForm";
import TextField from "../TextField/TextField";
import CustomButton from "../CustomButton/CustomButton";
import CustomDropdown from "../CustomDropdown/CustomDropdown";
import Message from "../../util/message";
import Constant from "../../util/constant";
import useGeoCountry from "../../hooks/useGeoCountry";
import "react-phone-number-input/style.css";
import PhoneInput, { isPossiblePhoneNumber } from "react-phone-number-input";
import { Controller } from "react-hook-form";
import Label from "../Label/Label";
import ThankYouPopUp from "../ThankYouPopUp/ThankYouPopUp";
import MeetingBookingModal from "../MeetingBookingModal/MeetingBookingModal";
import "./ContactUs.scss";
import { useDispatch, useSelector } from "react-redux";
import { getContactUsData } from "../../reduxThings/slices/DigipankSlices";
import {
  CONTACT_SERVICE_OPTIONS,
  submitContact,
} from "../../services/contactApi";
import {
  formatMeetingDateLabel,
  formatSlotTimeLabel,
} from "../../services/meetingApi";

const contactForm = {
  name: {
    name: "name",
    validate: {
      required: {
        value: true,
        message: Message.ERRORMESSAGE.FIRSTNAMEEMPTY,
      },
      pattern: {
        value: Constant.REGEX.Name,
        message: Message.ERRORMESSAGE.FIRSTNAMEEMPTY,
      },
    },
  },
  serviceType: {
    name: "serviceType",
    validate: {
      required: {
        value: true,
        message: "Please select a service.",
      },
    },
  },
  email: {
    name: "email",
    validate: {
      required: {
        value: true,
        message: Message.ERRORMESSAGE.EMAILEMPTY,
      },
      pattern: {
        value: Constant.REGEX.email,
        message: Message.ERRORMESSAGE.EMAILINVALID,
      },
    },
  },
  phoneNumber: {
    name: "phoneNumber",
    validate: {
      required: {
        value: true,
        message: Message.ERRORMESSAGE.CONTACTEMPTY,
      },
      validate: (value) =>
        isPossiblePhoneNumber(value || "") || Message.ERRORMESSAGE.MININVALID,
    },
  },
  projectDescription: {
    name: "projectDescription",
    validate: {},
  },
};

function ContactUs({ variant = "page" }) {
  const country = useGeoCountry();
  const dispatch = useDispatch();
  const [dimensions, setDimensions] = useState({
    height: window.innerHeight,
    width: window.innerWidth,
  });
  const [hidemodal, setHidemodal] = useState(false);
  const [submittedName, setSubmittedName] = useState("");
  const [meetingLink, setMeetingLink] = useState("");
  const [selectedMeeting, setSelectedMeeting] = useState(null);
  const [showMeetingModal, setShowMeetingModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const formRef = useRef(null);

  useEffect(() => {
    function handleResize() {
      setDimensions({
        height: window.innerHeight,
        width: window.innerWidth,
      });
    }

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    dispatch(getContactUsData());
  }, [dispatch]);

  const appData = useSelector(
    (e) => e?.DigipankSlice?.contactUsState?.contactUsStateData,
  );

  const getSubmitErrorMessage = (error) => {
    const apiMessage = error?.response?.data?.message;

    if (Array.isArray(apiMessage)) {
      return apiMessage.join(", ");
    }

    if (typeof apiMessage === "string") {
      return apiMessage;
    }

    return error?.message || "Failed to submit contact form.";
  };

  const onFormSubmit = async (data) => {
    setIsSubmitting(true);
    setSubmitError("");

    try {
      const response = await submitContact({
        name: data.name,
        serviceType: data.serviceType,
        email: data.email,
        phoneNumber: data.phoneNumber,
        projectDescription: data.projectDescription,
        ...(selectedMeeting || {}),
      });

      setSubmittedName(data.name);
      setMeetingLink(response?.meetingLink || "");
      formRef.current?.reset();
      setSelectedMeeting(null);
      setHidemodal(true);
    } catch (error) {
      const errorMessage = getSubmitErrorMessage(error);
      setSubmitError(errorMessage);
      window.alert(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!appData?.contactUs?.title) {
    return <div>..loading</div>;
  }

  return (
    <div
      className={`contact_Maindiv${variant === "modal" ? " contact_Maindiv--modal" : ""}`}
      style={variant === "modal" ? undefined : { minHeight: dimensions.height - 900 + "px" }}
      id={variant === "modal" ? undefined : "contact"}
    >
      <div className="center_Section">
        <div className="content_Section">
          <Label title={appData?.contactUs?.title}></Label>
          <Label title={appData?.contactUs?.desc}></Label>
        </div>

        <div className="input_Center">
          <HookForm
            defaultForm={{}}
            init={(form) => {
              formRef.current = form;
            }}
            onSubmit={onFormSubmit}
          >
            {(formMethod) => (
              <div className="form1">
                <div className="first_Line">
                  <div className="whole_Name">
                    <TextField
                      formMethod={formMethod}
                      rules={contactForm.name.validate}
                      name={contactForm.name.name}
                      errors={formMethod?.errors}
                      autoFocus={true}
                      type="text"
                      placeholder={appData?.contactUs?.form?.namePlaceholder}
                    />
                    <span className="colorRed required-mark">*</span>
                  </div>
                  <div className="service">
                    <Controller
                      name={contactForm.serviceType.name}
                      control={formMethod.control}
                      rules={contactForm.serviceType.validate}
                      render={({ field: { onChange, value } }) => {
                        const selectedService = CONTACT_SERVICE_OPTIONS.find(
                          (item) => item.id === value,
                        );

                        return (
                          <CustomDropdown
                            selectedValue={selectedService || null}
                            dropDownItems={CONTACT_SERVICE_OPTIONS}
                            placeholder={
                              appData?.contactUs?.form?.servicePlaceholder
                            }
                            onSelect={(item) => onChange(item?.id || "")}
                          />
                        );
                      }}
                    />
                    <span className="colorRed required-mark">*</span>
                    {formMethod?.errors?.serviceType && (
                      <div className="errorMsg show service-error">
                        <p>{formMethod.errors.serviceType.message}</p>
                      </div>
                    )}
                  </div>
                </div>
                <div className="second_Line">
                  <div className="email_Div">
                    <TextField
                      formMethod={formMethod}
                      rules={contactForm.email.validate}
                      name={contactForm.email.name}
                      errors={formMethod?.errors}
                      type="email"
                      placeholder={appData?.contactUs?.form?.emailPlaceolder}
                    />
                    <span className="colorRed required-mark">*</span>
                  </div>
                  <div className="phone_Sec">
                    <Controller
                      name={contactForm.phoneNumber.name}
                      control={formMethod.control}
                      rules={contactForm.phoneNumber.validate}
                      render={({ field: { onChange, value } }) => (
                        <PhoneInput
                          key={country}
                          defaultCountry={country}
                          addInternationalOption={false}
                          value={value}
                          onChange={onChange}
                          placeholder={
                            appData?.contactUs?.form?.phonePlaceholder
                          }
                        />
                      )}
                    />
                    <span className="colorRed phone-required-mark">*</span>
                    {formMethod?.errors?.phoneNumber && (
                      <div className="errorMsg show phone-error">
                        <p>{formMethod.errors.phoneNumber.message}</p>
                      </div>
                    )}
                  </div>
                </div>
                <div className="project-description-field">
                  <div className="project-description-row">
                    <TextField
                      formMethod={formMethod}
                      rules={contactForm.projectDescription.validate}
                      name={contactForm.projectDescription.name}
                      errors={formMethod?.errors}
                      textarea="textarea"
                      rows={3}
                      placeholder={
                        appData?.contactUs?.form?.descriptionPlaceholder ||
                        "Give a brief description about your project"
                      }
                    />
                    <button
                      type="button"
                      className="book-meeting-link"
                      onClick={() => setShowMeetingModal(true)}
                    >
                      Book meeting slots
                    </button>
                  </div>
                  {selectedMeeting && (
                    <p className="selected-meeting-summary">
                      Meeting selected:{" "}
                      {formatMeetingDateLabel(selectedMeeting.meetingDate)}{" "}
                      {formatSlotTimeLabel(selectedMeeting.meetingStartTime)} –{" "}
                      {formatSlotTimeLabel(selectedMeeting.meetingEndTime)}
                      <button
                        type="button"
                        className="clear-meeting-link"
                        onClick={() => setSelectedMeeting(null)}
                      >
                        Clear
                      </button>
                    </p>
                  )}
                </div>
                {submitError && (
                  <p className="contact-submit-error">{submitError}</p>
                )}
                <div className="submit_Btn">
                  <CustomButton
                    title={
                      isSubmitting
                        ? "Submitting..."
                        : appData?.contactUs?.form?.buttonTitle
                    }
                    type="submit"
                  />
                  <i className="icon-arrow"></i>
                </div>
              </div>
            )}
          </HookForm>
        </div>
      </div>
      <ThankYouPopUp
        showModal={hidemodal}
        userName={submittedName}
        meetingLink={meetingLink}
        onHide={() => {
          setHidemodal(false);
          setMeetingLink("");
        }}
        onHideClick={() => {
          setHidemodal(false);
          setMeetingLink("");
        }}
        onContinue={() => {
          setHidemodal(false);
          setMeetingLink("");
        }}
      />
      <MeetingBookingModal
        show={showMeetingModal}
        onHide={() => setShowMeetingModal(false)}
        initialSelection={selectedMeeting}
        onConfirm={setSelectedMeeting}
      />
    </div>
  );
}

export default ContactUs;
